<template>
  <AppDroplist
    v-if="!readonly"
    :append-to="appendTo"
    :dropdown-min-width="540"
    :model-value="openDialog"
    :offset="offset"
    :position="position"
    :to-selector="toSelector"
    max-width="640px"
    show-on-init
    trigger="manual"
    @close="onClose"
    @hidden="onHidden"
    @open="onOpen"
    @opened="onDialogOpened"
  >
    <div
      v-click-away="onClickOutsideContent"
      :class="{
        'gs-Content-task': isJiraIssueOrWorkItem,
        'gs-Content-threshold': isThreshold
      }"
      class="gs-Content"
    >
      <div class="gs-ContentWrapper">
        <div class="gs-ContentHeader">
          <DatePicker
            v-model="backdate"
            :append-to-body="false"
            :class="{
              'gs-Datepicker-disabled': isDatePickerDisabled
            }"
            :clearable="false"
            :disabled="isDatePickerDisabled"
            :disabled-date="notAfterToday"
            :editable="false"
            :format="DATE_FORMAT"
            :lang="getDatepickerOptions"
            :popup-style="{ left: 0 }"
            class="gs-Datepicker"
            popup-class="dp-Datepicker"
          >
            <template #input="{ class: className, value }">
              <div
                :class="{
                  [className]: true
                }"
                class="gs-Datepicker_Input"
              >
                {{ calendarPlaceholder || value }}
              </div>
            </template>
            <template #icon-calendar>
              <AppIcon
                class="gs-Datepicker_Chevron"
                height="24"
                icon-name="arrow-down-black-next"
                width="24"
              />
            </template>
          </DatePicker>
          <AnimatedNumber
            v-slot="{ displayValue }"
            :end-amount="localGradeDisplayValue"
            skip-initial-animation
          >
            <OkrGradeValue
              v-if="isShowGradeValue"
              :grade="Number(displayValue)"
              class="gs-Grade"
              data-auto-testid="grade"
            />
          </AnimatedNumber>
        </div>

        <div class="gs-ValuesWrapper">
          <div class="gs-Content_Values">
            <FormFieldNext
              v-if="!hasValueTypeBinary && !isThreshold"
              :label="$t('create.keyResult.label.originalValue')"
              class="gs-ValueField"
              disabled
            >
              <ProgressPreviewValueField
                :model-value="getOriginalValue"
                :suffix="getPercentSuffix"
                class="gs-Input"
                data-auto-testid="start-value"
                data-testid="start-value"
              />
            </FormFieldNext>

            <FormFieldNext
              v-tippy="{
                content: isValueFieldDisabled ? $t('current_value.disabled_tooltip') : ''
              }"
              :class="{
                'gs-ValueField': true,
                'gs-ValueField-binary': hasValueTypeBinary
              }"
              :disabled="isValueFieldDisabled"
              :label="$t('common.value')"
            >
              <BinaryValueSwitch
                v-if="hasValueTypeBinary"
                :disabled="isValueFieldDisabled"
                :model-value="localCurrentValue"
                name="grade-form-binary-type-switch"
                @update:model-value="changeBinaryValue"
              />

              <AppInputNumberNext
                v-else
                ref="currentValueInput"
                v-model="getLocalCurrentValue"
                :digit-max-length="NUMBER_INPUT_DIGITS.ELEMENT_VALUES"
                :fraction="NUMBER_INPUT_FRACTIONS.ELEMENT_VALUES"
                :readonly="isValueFieldDisabled"
                :suffix="getPercentSuffix"
                allow-empty-value
                allow-negative
                class="gs-Input"
                data-auto-testid="current-value"
                full-width
                show-previous-value
              />
            </FormFieldNext>

            <template v-if="isThreshold">
              <FormFieldNext :label="$t('objectives.threshold')" class="gs-ValueField" disabled>
                <ProgressPreviewValueField
                  :model-value="getTargetValue"
                  :prefix="getThresholdPrefix"
                  :suffix="getPercentSuffix"
                  class="gs-Input"
                  data-auto-testid="target-value"
                  data-testid="target-value-field"
                />
              </FormFieldNext>
            </template>

            <FormFieldNext
              v-if="!hasValueTypeBinary && !isThreshold"
              :label="$t('create.keyResult.label.targetValue')"
              class="gs-ValueField"
              disabled
            >
              <ProgressPreviewValueField
                :model-value="getTargetValue"
                :suffix="getPercentSuffix"
                class="gs-Input"
                data-auto-testid="target-value"
                data-testid="target-value-field"
              />
            </FormFieldNext>
          </div>
        </div>

        <div class="gs-Content_Footer">
          <div v-if="isProgressLoading" class="gs-Loader" />

          <template v-if="isTodaySelected">
            <AppSelect
              v-if="isJiraIssueOrWorkItem"
              :model-value="localGradeMode"
              :offset="[0, 0]"
              :options="gradeModeOptions"
              append-to="parent"
              class="gs-AutoModeSelect"
              dropdown-width="200px"
              @update:model-value="onUpdateGradeMode"
            >
              <template #option-label="{ option }">
                <div class="gs-AutoModeSelectLabel">
                  <AppIcon
                    :icon-name="option.icon"
                    class="gs-AutoModeSelect-LabelIcon"
                    height="24"
                    width="24"
                  />
                  {{ option.label }}
                </div>
              </template>
              <template #button="{ option, active }">
                <div
                  v-if="option"
                  :class="{ 'gs-SelectButton-active': active }"
                  class="gs-SelectButton"
                >
                  <AppIcon
                    :icon-name="option.icon"
                    class="gs-AutoModeSelect-ButtonIcon"
                    height="24"
                    width="24"
                  />
                  {{ option.label }}
                  <AppIcon
                    class="gs-SelectChevron"
                    height="24"
                    icon-name="arrow-down-black-next"
                    width="24"
                  />
                </div>
              </template>
            </AppSelect>

            <StatusSelect
              v-else
              :grade="gradeForConfidenceLevel"
              :objective="objectiveForStatus"
              append-to="parent"
              autocalculate-toggle
              data-auto-testid="status-switch"
              @update:confidence-level-id="onUpdateLocalConfidenceLevelId"
            >
              <template #button="{ option, opened, disabled, basedOnGrade }">
                <!--              <ObjectiveStatus
                  :status="getStatusValueForDisplay(option.value)"
                  class="gs-ObjectiveStatus"
                  data-auto-testid="status-switch-trigger"
                >
                  <template #default="{ label }">
                    <span class="oboard-truncated-text">{{ label }}</span>
                  </template>
                  <template #after>
                    <AppIcon
                      class="gs-ObjectiveStatus_Chevron"
                      height="24"
                      icon-name="arrow-down-black"
                      width="24"
                    />
                  </template>
                </ObjectiveStatus>-->

                <FormStatusButton
                  v-if="option"
                  :based-on-grade="basedOnGrade"
                  :disabled="disabled"
                  :opened="opened"
                  :option="getStatusOptionForDisplay(option, basedOnGrade)"
                />
              </template>
            </StatusSelect>
          </template>

          <div class="gs-FooterActions">
            <AppButton data-auto-testid="cancel-button" type="ghost-next" @click="cancelEditValue">
              {{ $t('action.cancel') }}
            </AppButton>

            <AppButton
              :disable="isConfirmButtonDisabled"
              :loading="isLoading || isProgressLoading"
              data-auto-testid="confirm-button"
              type="primary-next"
              @click="save"
            >
              {{ confirmButtonText }}
            </AppButton>
          </div>
        </div>
      </div>
    </div>
  </AppDroplist>
</template>

<script>
import { vOnClickOutside } from '@vueuse/components'
import dayjs from 'dayjs'
import { debounce, isNull } from 'lodash'
import { defineComponent } from 'vue'
import DatePicker from 'vue2-datepicker'

import ObjectivesApiHandler from '@/api/okr-elements'
import { tracker } from '@/tracking/amplitude'
import {
  EVENT_CATEGORIES,
  EVENT_SOURCES,
  trackManualUpdateGradeEvent
} from '@/tracking/amplitude-helpers'
import {
  NUMBER_INPUT_DIGITS,
  NUMBER_INPUT_FRACTIONS
} from '@/utils/components-configurations/app-input-number'
import { datepickerLang, getStartOfTheDay, localDateToUtc } from '@/utils/date'
import { OKR_ELEMENT_ENTITY_KEYS, REQUEST_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { isStringEmpty } from '@/utils/general'
import { isEnter } from '@/utils/key-codes'
import { KR_VALUE_TYPES } from '@/utils/metrics-helpers'
import {
  isJiraIssueOrWorkItem,
  isKR,
  objectiveIsJiraTask,
  TYPE_ID_NAMES
} from '@/utils/objective-types'
import { ALL_STATUS_OPTIONS, OBJECTIVE_STATUS_AUTO, OKR_STATUSES } from '@/utils/objectives'
import { useMetricValues } from '@/utils/okr-element-composables'
import {
  AUTO_GRADE_OPTION_VALUE,
  AZURE_WORK_ITEM_AUTO_GRADE_OPTION,
  DEFAULT_BINARY_TARGET_VALUE,
  DEFAULT_CURRENT_VALUE,
  DEFAULT_RESULT_TYPE_VALUES,
  DEFAULT_TARGET_VALUE,
  GRADE_MODES,
  isBinaryProgress,
  isThresholdProgress,
  JIRA_ISSUE_AUTO_GRADE_OPTION
} from '@/utils/okr-element-values'
import { getGradeDisplayValue } from '@/utils/okr-elements/grade'
import { objectOrNullProp } from '@/utils/prop-validators'

import AppDroplist from '@/components/AppDroplist'
import BinaryValueSwitch from '@/components/form/element-progress-preview/BinaryValueSwitch'
import ProgressPreviewValueField from '@/components/form/element-progress-preview/ProgressPreviewValueField'
import FormFieldNext from '@/components/form/FormFieldNext'
import OkrGradeValue from '@/components/form/OkrGradeValue'
import StatusSelect from '@/components/objectives/status-select/StatusSelect'
import AnimatedNumber from '@/components/ui/AnimatedNumber/AnimatedNumber'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppInputNumberNext from '@/components/ui/AppInputNumberNext/AppInputNumberNext'
import AppSelect from '@/components/ui/AppSelect/AppSelect'
import FormStatusButton from '@/components/ui/AppSelect/TriggerButtons/FormStatusButton'

const objectivesApi = new ObjectivesApiHandler()

const DATE_FORMAT = 'D MMM YYYY'

export default defineComponent({
  name: 'GradeSelect',

  directives: [vOnClickOutside],

  components: {
    BinaryValueSwitch,
    ProgressPreviewValueField,
    AnimatedNumber,
    FormStatusButton,
    DatePicker,
    OkrGradeValue,
    AppDroplist,
    AppSelect,
    AppIcon,
    AppButton,
    FormFieldNext,
    AppInputNumberNext,
    StatusSelect
  },

  props: {
    readonly: {
      type: Boolean
    },

    objective: {
      type: Object,
      required: true
    },

    // showMultiplier: {
    //   type: Boolean,
    //   default: true
    // },

    toSelector: {
      type: String,
      default: null
    },

    appendTo: {
      type: String,
      default: '#app'
    },

    show: {
      type: Boolean
    },

    presetBackdate: {
      type: [String, Date, Number],
      default: null
    },

    position: {
      type: String,
      default: 'bottom'
    },

    offset: {
      type: Array,
      default: () => [0, 8]
    },

    viewConfig: {
      validator: v => objectOrNullProp(v),
      default: null
    }

    // elementTargetPeriods: {
    //   type: Array,
    //   default: () => DEFAULT_MILESTONES_SETTINGS[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS]
    // }
  },

  emits: {
    'update:is-loading': null,
    'error-on-save': null,
    save: null,
    // click: null,
    'update:show': null
  },

  setup(props) {
    const { getThresholdPrefix, getOriginalValue, unit } = useMetricValues({
      element: () => props.objective
    })

    return {
      getThresholdPrefix,
      getOriginalValue,
      unit
    }
  },

  data() {
    return {
      // uid: uid(),
      localFieldTypeId: this.objective.fieldTypeId,
      localGradeValue: this.objective.gradeToUse,
      localCurrentValue: this.objective.currentValue,

      openDialog: this.show,
      localConfidenceLevelId: this.objective.confidenceLevelId || OKR_STATUSES.AUTO,
      localAutomaticConfidenceLevelId:
        this.objective.automaticConfidenceLevelId || OKR_STATUSES.AUTO,

      /* v-click-away on dialog content is triggered also on dialog opening, `dialogIsOpened`
       * is used to recognize opening and other cases
       */
      dialogIsOpened: false,
      // editInitialised: false,

      isLoading: false,
      isProgressLoading: false,
      localGradeMode: this.objective.gradeMode,

      backdate: null
    }
  },

  computed: {
    NUMBER_INPUT_FRACTIONS: () => NUMBER_INPUT_FRACTIONS,

    NUMBER_INPUT_DIGITS: () => NUMBER_INPUT_DIGITS,

    DATE_FORMAT: () => DATE_FORMAT,

    getBackdateAsRequestParameter() {
      if (this.isTodaySelected) return null

      return localDateToUtc(dayjs(this.backdate).startOf('day'))
    },

    gradeForConfidenceLevel() {
      return Number(this.localGradeValue)

      // const defaultValue = Number(this.localGradeValue)

      // if (this.localConfidenceLevelId === OKR_STATUSES.NOT_STARTED) {
      //   return defaultValue
      // }

      // if (this.isJiraIssueOrWorkItem || this.isKR) {
      //   if (
      //     isBinaryProgress({
      //       element: this.objective
      //     })
      //   ) {
      //     return defaultValue
      //   }

      //   const selectedBackDate = this.backdate || this.today

      //   const isThreshold = isThresholdProgress({
      //     element: this.objective
      //   })

      //   const compatiblePeriod = this.elementTargetPeriods.find(period => {
      //     if (isThreshold) {
      //       return dayjs(selectedBackDate).isBetween(
      //         dayjs(period.startDate),
      //         dayjs(period.endDate),
      //         DATE_UNITS.DAY,
      //         '[]'
      //       )
      //     }

      //     return (
      //       dayjs(selectedBackDate).format(ELEMENT_PERIODS_DATE_FORMAT) ===
      //       dayjs(period.endDate).format(ELEMENT_PERIODS_DATE_FORMAT)
      //     )
      //   })

      //   // only in this way
      //   // DO NOT USE || OPERATOR
      //   // because 0 is valid value
      //   const targetValue = compatiblePeriod ? compatiblePeriod.targetValue : this.getTargetValue

      //   return calculateGradeValue(targetValue, this.getOriginalValue, this.localCurrentValue)
      // }

      // return defaultValue
    },

    gradeValueTriggers() {
      // return [this.localCurrentValue, this.getTargetValue]
      return [this.localCurrentValue, this.backdate]
    },

    getDatepickerOptions() {
      return datepickerLang()
    },

    today() {
      return getStartOfTheDay(dayjs())
    },

    isTodaySelected() {
      return isNull(this.backdate) || Number(this.backdate) === Number(this.today)
    },

    isShowGradeValue() {
      return !this.isJiraIssueOrWorkItem || !this.isAutoMode

      //if (this.isJiraIssueOrWorkItem) {
      //  return !this.isAutoMode
      //}
      //
      //return !this.isThreshold
    },

    isValueFieldDisabled() {
      return this.isJiraIssueOrWorkItem && this.isAutoMode
    },

    isDatePickerDisabled() {
      return this.isValueFieldDisabled || !isNull(this.presetBackdate)
    },

    calendarPlaceholder() {
      return this.isTodaySelected ? this.$t('filter.today') : null
    },

    confirmButtonText() {
      return this.$t('action.confirm')
      // return this.isTodaySelected
      //   ? this.$t('action.confirm')
      //   : this.$t('common.set_on_date', { date: dayjs(this.backdate).format(DATE_FORMAT) })
    },

    isAutoMode() {
      return this.localGradeMode === GRADE_MODES.AUTO
    },

    getPercentSuffix() {
      return this.unit
    },

    getTargetValue() {
      if (this.hasValueTypeBinary) return DEFAULT_BINARY_TARGET_VALUE

      return isNull(this.objective.targetValue) ? DEFAULT_TARGET_VALUE : this.objective.targetValue // after link jira-issue in autoMode we get null from backend

      /**
       * temporary or not commented because of the issue
       * @link https://oboard.atlassian.net/browse/OK-3449
       * @see comments section from Yaroslav

      const selectedBackDate = this.backdate || this.today

       const generalTargetValue = isNull(this.objective.targetValue) ? DEFAULT_TARGET_VALUE : this.objective.targetValue // after link jira-issue in autoMode we get null from backend

      const compatiblePeriod = this.elementTargetPeriods.find(period => {
        return dayjs(selectedBackDate).isBetween(
          dayjs(period.startDate),
          dayjs(period.endDate),
          'day',
          '[]'
        )
      })

      // only in this way
      // DO NOT USE || OPERATOR
      // because 0 is valid value
      return compatiblePeriod ? compatiblePeriod.targetValue : generalTargetValue

       */
    },

    getLocalCurrentValue: {
      get() {
        if (this.isJiraIssueOrWorkItem) {
          return this.localCurrentValue ?? DEFAULT_CURRENT_VALUE // after link jira-issue in autoMode we get null from backend
        }

        return this.localCurrentValue ?? ''
      },

      set(newValue) {
        if (isStringEmpty(String(newValue))) {
          this.localCurrentValue = null
        } else {
          this.localCurrentValue = newValue
        }
      }
    },

    isJiraIssue() {
      return objectiveIsJiraTask(this.objective)
    },

    gradeModeOptions() {
      const icon = this.isJiraIssue
        ? JIRA_ISSUE_AUTO_GRADE_OPTION.icon
        : AZURE_WORK_ITEM_AUTO_GRADE_OPTION.icon

      return [
        { label: this.$t('status.switch_auto'), value: GRADE_MODES.AUTO, icon },
        {
          label: this.$t('status.switch_manual'),
          value: GRADE_MODES.MANUAL,
          icon: 'period-mode-manual-next'
        }
      ]
    },

    isJiraIssueOrWorkItem() {
      return isJiraIssueOrWorkItem(this.objective)
    },

    isKR() {
      return isKR(this.objective)
    },

    hasValueTypeBinary() {
      return isBinaryProgress({
        element: {
          ...this.objective,
          fieldTypeId: this.localFieldTypeId
        }
      })
    },

    isThreshold() {
      return isThresholdProgress({
        element: {
          ...this.objective,
          fieldTypeId: this.localFieldTypeId
        }
      })
    },

    isConfirmButtonDisabled() {
      return (
        !this.hasValueTypeBinary &&
        (isNull(this.getLocalCurrentValue) || isStringEmpty(String(this.getLocalCurrentValue)))
      )
    },

    localGradeDisplayValue() {
      return getGradeDisplayValue(this.localGradeValue)
    },

    // localGradeDisplayValue: {
    // get() {
    //   return getGradeDisplayValue(this.localGradeValue)
    // }

    // set(newValue) {
    //   this.localGradeValue = newValue
    //   this.calculateCurrentValue()
    // }
    // },

    objectiveForStatus() {
      return {
        ...this.objective,
        confidenceLevelId: this.localConfidenceLevelId,
        automaticConfidenceLevelId: this.localAutomaticConfidenceLevelId
      }
    }

    // color() {
    //   return this.objective.gradeColor
    // }
  },

  watch: {
    openDialog(newValue) {
      if (!newValue) {
        this.dialogIsOpened = false
      }
      this.localGradeMode = this.objective.gradeMode
      this.$emit('update:show', newValue)
    },

    show(newValue) {
      this.openDialog = newValue
    },

    gradeValueTriggers: {
      handler(newValue) {
        const [localCurrentValue] = newValue

        if (this.dialogIsOpened) {
          this.debouncedGetActualGradeValue({
            value: localCurrentValue
          })
        }
      },

      deep: true
    }
  },

  mounted() {
    document.addEventListener('keydown', this.onDocumentKeydown)
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.onDocumentKeydown)
  },

  created() {
    this.debouncedGetActualGradeValue = debounce(
      ({ value = 0 } = {}) => this.setActualProgress({ value }),
      500
    )
  },

  methods: {
    async setActualProgress({ value = 0 } = {}) {
      if (!this.dialogIsOpened) return

      this.isProgressLoading = true
      try {
        const { grade, autoConfidenceLevelId } = await objectivesApi.calculateProgressForElement({
          elementId: this.objective.id,
          value: Number(value),
          date: this.getBackdateAsRequestParameter
        })

        this.localGradeValue = grade
        this.localAutomaticConfidenceLevelId = autoConfidenceLevelId || OKR_STATUSES.AUTO

        if (this.localConfidenceLevelId === OKR_STATUSES.NOT_STARTED) {
          this.localConfidenceLevelId = OKR_STATUSES.AUTO
        }
      } catch (error) {
        handleError({ error })
      } finally {
        this.isProgressLoading = false
      }
    },

    getStatusOptionForDisplay(option, basedOnGrade) {
      // it means that auto status mode selected
      if (!this.isTodaySelected && basedOnGrade) {
        return {
          ...OBJECTIVE_STATUS_AUTO,
          label: this.$t(OBJECTIVE_STATUS_AUTO.label)
        }
      }

      return option
    },

    notAfterToday(date) {
      return date.getTime() > new Date().getTime()
    },

    onUpdateLocalConfidenceLevelId(value) {
      this.localConfidenceLevelId = value
    },

    onUpdateGradeMode(value) {
      this.localGradeMode = value
      this.localFieldTypeId =
        this.objective.fieldTypeId === AUTO_GRADE_OPTION_VALUE
          ? KR_VALUE_TYPES.START_TARGET
          : this.objective.fieldTypeId || KR_VALUE_TYPES.START_TARGET
    },

    cancelEditValue() {
      this.dialogIsOpened = false
      this.openDialog = false

      this.backdate = null
      this.localFieldTypeId = this.objective.fieldTypeId || DEFAULT_RESULT_TYPE_VALUES.fieldTypeId
      this.localGradeValue = this.objective.gradeToUse
      this.localCurrentValue = this.objective.currentValue
      this.localConfidenceLevelId = this.objective.confidenceLevelId || OKR_STATUSES.AUTO
      this.localAutomaticConfidenceLevelId =
        this.objective.automaticConfidenceLevelId || OKR_STATUSES.AUTO
    },

    changeBinaryValue(value) {
      this.localCurrentValue = value
    },

    async save() {
      this.isLoading = true
      this.$emit('update:is-loading', true)

      let confidenceLevelId = this.localConfidenceLevelId

      if (!this.isTodaySelected) {
        confidenceLevelId = undefined
      }

      let payload = {
        elementId: this.objective.id,
        parentId: this.objective.parentId,
        jiraIssueId: this.objective.jiraIssueId,
        currentValue: +this.localCurrentValue,
        originalValue: this.getOriginalValue,
        targetValue: this.getTargetValue,
        workspaceId: this.objective.workspaceId,
        dueDate: this.objective.dueDate,
        elementStartDate: this.objective.elementStartDate,
        fieldTypeId: this.localFieldTypeId,
        confidenceLevelId,
        backdate: this.getBackdateAsRequestParameter,
        [REQUEST_ENTITY_KEYS.VIEW_CONFIG]: this.viewConfig,
        [OKR_ELEMENT_ENTITY_KEYS.TYPE_ID]: this.objective[OKR_ELEMENT_ENTITY_KEYS.TYPE_ID]
      }
      if (this.isJiraIssueOrWorkItem && this.isTodaySelected) {
        payload = {
          ...payload,
          gradeMode: this.localGradeMode
        }
      }
      if (this.localGradeMode === GRADE_MODES.AUTO && this.isJiraIssueOrWorkItem) {
        payload = {
          ...payload,
          currentValue: undefined,
          originalValue: undefined,
          targetValue: undefined
        }
      }
      try {
        const result = await objectivesApi.updateOkrElement(payload)

        if (this.localConfidenceLevelId !== this.objective.confidenceLevelId) {
          const statusValue = ALL_STATUS_OPTIONS.map(option => ({
            ...option,
            label: this.$t(option.label)
          })).find(item => item.value === this.localConfidenceLevelId)
          tracker.logEvent('Changed okr status', {
            category: EVENT_CATEGORIES.OKR_MANAGEMENT,
            label: 'kr',
            source: EVENT_SOURCES.GRID,
            value: this.localConfidenceLevelId ? statusValue.label : 'auto'
          })
        }

        this.$emit('save', result)

        if (this.isKR || (this.localGradeMode !== GRADE_MODES.AUTO && this.isJiraIssueOrWorkItem)) {
          const confidenceLevel = this.isKR
            ? this.localConfidenceLevelId
            : this.objective.jiraStatus

          let value = payload.currentValue

          if (this.hasValueTypeBinary) {
            value = payload.currentValue * 100 // for convert boolean to percent
          }

          trackManualUpdateGradeEvent({
            confidenceLevel,
            value,
            source: EVENT_SOURCES.GRID,
            id: this.objective.id,
            issueType: TYPE_ID_NAMES[this.objective.typeId]
          })
        }

        this.dialogIsOpened = false
        this.openDialog = false
      } catch (e) {
        this.$emit('error-on-save', e)
      } finally {
        this.isLoading = false
        this.$emit('update:is-loading', false)
      }
    },

    applyBackdate() {
      const resolvedBackdate = dayjs(this.presetBackdate).isValid()
        ? getStartOfTheDay(dayjs(this.presetBackdate))
        : null

      this.backdate =
        resolvedBackdate && Number(resolvedBackdate) < Number(this.today) ? resolvedBackdate : null
    },

    onOpen() {
      this.applyBackdate()

      this.$nextTick(() => {
        this.dialogIsOpened = true
      })

      this.localFieldTypeId = this.objective.fieldTypeId || DEFAULT_RESULT_TYPE_VALUES.fieldTypeId
      this.localGradeValue = this.objective.gradeToUse
      this.localCurrentValue = this.objective.currentValue
      // this.$refs.currentValueInput?.updateMask()
      // this.$refs.targetValueInput?.updateMask()
    },

    onDialogOpened() {
      // for start/target type focus on current value
      if (
        [KR_VALUE_TYPES.START_TARGET, KR_VALUE_TYPES.THRESHOLD].includes(this.localFieldTypeId) &&
        !this.isValueFieldDisabled
      ) {
        this.$refs.currentValueInput?.focus()

        if (
          !isNull(this.getLocalCurrentValue) &&
          !isStringEmpty(String(this.getLocalCurrentValue))
        ) {
          this.$refs.currentValueInput?.selectAll()
        }
      }
    },

    onHidden() {
      if (this.dialogIsOpened) {
        this.dialogIsOpened = false
        this.openDialog = false
      }
    },

    onClose() {
      this.backdate = null
      this.localGradeValue = this.objective.gradeToUse
      this.localCurrentValue = this.objective.currentValue
      this.localConfidenceLevelId = this.objective.confidenceLevelId || OKR_STATUSES.AUTO
      this.localAutomaticConfidenceLevelId =
        this.objective.automaticConfidenceLevelId || OKR_STATUSES.AUTO
    },

    onDocumentKeydown(event) {
      const eventCode = event.keyCode ? event.keyCode : event.which
      if (this.openDialog && isEnter(eventCode)) {
        this.save()
      }
    },

    // onClickOutsideContent(event) {
    //   // click outside closes grade dialog, but ignore status dropdown that is outside of dialog
    //   if (this.openDialog) {
    //     if (this.dialogIsOpened && !event.target.matches(`.gs-StatusDropdown-${this.uid} *`)) {
    //       this.openDialog = false
    //     } else {
    //       this.dialogIsOpened = true
    //       this.$refs.droplist.show()
    //     }
    //   }
    // }

    onClickOutsideContent(e) {
      if (this.openDialog) {
        if (
          Array.from(e.composedPath()).some(item => {
            const { classList } = item
            return classList?.contains('gc-Grade-updatable') || classList?.contains('gs-Datepicker')
          })
        ) {
          return
        }
        this.cancelEditValue()
      }
    }
  }
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/interval-modal';

.gs-ContentHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  margin-bottom: 17px;
}

.gs-Datepicker {
  @extend %interval-datepicker-styles;
  width: auto;

  :deep(.mx-icon-calendar) {
    right: 0;
  }
}

.gs-Datepicker_Input {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: fw('semi-bold');
  --datepicker-input-padding: 0px 24px 0px 0px;
  border: none;
  height: 32px;
}

.gs-Datepicker_Input,
.gs-Datepicker_Chevron {
  .gs-Datepicker-disabled & {
    cursor: not-allowed;
    color: $grey-1-next;
  }
}

.gs-Content {
  --field-width: 156px;

  &-threshold {
    --field-width: 240px;
  }
}

.gs-ContentWrapper {
  padding: 6px 20px 18px;
  &-withoutContentValues {
    padding-top: 0;
    .gs-Content_Footer {
      margin-top: 0;
    }
  }
}

.gs-Content_Values {
  display: flex;
  gap: 12px;
  width: 100%;
}

.gs-ValuesWrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 44px;
}

.gs-Content_Footer {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 13px;
  padding-top: 14px;
  box-shadow: inset 0 1px 0 $grey-2-next;
}

.gs-FooterActions {
  display: inherit;
  gap: 8px;
  margin-left: auto;
}

$max-width-container: 380px;

.gs-AutoModeWrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: $max-width-container;
}

.gs-GradeSelectText {
  max-width: $max-width-container;
  overflow: hidden;
  text-overflow: ellipsis;
}

/*.gs-AutoModeSelect-ButtonIcon {
  margin: 0 4px 0 0;
}*/
.gs-SelectButton {
  font-size: $fs-12;
  line-height: 16px;
  font-weight: fw('bold');
  font-family: $system-ui;
  color: $dark-2;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  background-color: $grey-3-next;
  border-radius: $border-radius-sm-next;

  &-active {
    color: $primary-color-next;
  }
}
.gs-SelectChevron {
  flex: 0 0 auto;
  transition: transform $transition-fast ease-in-out;
  .gs-SelectButton-active & {
    transform: rotate(180deg);
    color: $primary-color-next;
  }
}
.gs-AutoModeSelect-LabelIcon {
  color: $dark-3;
}
.gs-AutoModeSelect-ButtonIcon {
  color: $dark-2;
}

.gs-AutoModeSelectLabel {
  display: flex;
  align-items: center;
  gap: 4px;
}
.gs-AutoModeInfoWrapper {
  display: flex;
  justify-content: space-between;
  background: $grey-3-next;
  border-radius: $border-radius-sm-next;
  width: 100%;
  gap: 15px;
}
.gs-AutoModeLeftSide {
  display: flex;
  margin: 8px 0 18px 8px;
  gap: 8px;
}
.gs-AutoModeLeftSide_Text {
  font-weight: fw('semi-bold');
  font-size: $fs-14;
  line-height: 20px;
  color: $dark-1;
  max-width: 280px;
  white-space: pre-line;
  margin: 12px 0 0 0;
}
.gs-AutoModeRightSide {
  display: flex;
  background: top right url('~@/assets/images/jira-in-progress.svg') no-repeat, $white;
  width: 140px;
  flex: 0 0 auto;
  border: 1px solid $grey-3-next;
  border-radius: $border-radius-sm-next;
  position: relative;

  &:after {
    pointer-events: none;
    content: '';
    position: absolute;
    right: -1px;
    bottom: -1px;
    width: calc(100% + 1px);
    height: calc(100% - 28px);
    background: linear-gradient(to bottom, rgba($white, 0) 0%, $white 55px, $white 100%);
    border-radius: 0 0 $border-radius-sm-next $border-radius-sm-next;
  }
}
.gs-ObjectiveStatus {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 4px;
  width: inherit;
  justify-content: space-between;
  --padding: 6px 6px 6px 8px;
}

.gs-ObjectiveStatus_Chevron {
  flex-shrink: 0;
}

.gs-Grade {
  --value-font-size: #{$fs-22};
  --suffix-font-size: #{$fs-16};
  --suffix-font-weight: #{fw('semi-bold')};
  --value-max-width: 10ch;
  --value-font-weight: #{fw('bold')};
  --value-line-height: 24px;
}

.gs-ValueField {
  width: var(--field-width);

  &-binary {
    width: 100%;
    min-width: 492px;
  }
}

.gs-Input {
  --font-size: #{$fs-14};
}

.gs-SelectButtonLabel {
  font-weight: fw('semi-bold');
}

.gs-DisabledValue {
  --readonly-color: #{$dark-3};
  --readonly-border-color: #{$grey-2-next};
  --readonly-background-color: #{$grey-2-next};
  --input-font-weight: #{fw('semi-bold')};
}

.gs-Loader {
  width: 100%;
  height: 1px;
  overflow: hidden;
  position: absolute;
  left: 0;
  top: 0;

  &:before {
    content: '';
    position: absolute;
    left: -50%;
    height: 100%;
    width: 40%;
    background-color: $primary-color-next;
    animation: lineAnim 1s linear infinite;
  }
}

@keyframes lineAnim {
  0% {
    left: -40%;
  }
  50% {
    left: 20%;
    width: 80%;
  }
  100% {
    left: 100%;
    width: 100%;
  }
}
</style>
